import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, LinkIcon, AlertTriangle } from "lucide-react";
import apiUtils from "../utils/apiUtils";
import usePageMeta from "../hooks/usePageMeta";

/**
 * ResetPassword - serves both "reset a forgotten password" and "set your first
 * password from a pilot invitation"; both arrive here with a one-time token.
 *
 * A rejected token is a DEAD END, not a form error: no amount of retyping will
 * make an expired or superseded link work. So the form is replaced with a way
 * out rather than left on screen to be resubmitted forever.
 */
export default function ResetPassword() {
  usePageMeta({
    title: "Set a New Password · RepCheck",
    description: "Choose a new password for your RepCheck account.",
  });

  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [linkDead, setLinkDead] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiUtils.post("/api/users/reset-password", { token, password });
      setDone(res?.data?.message || true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || "";
      // 400 from this endpoint means the token itself was refused (bad, already
      // used, or expired) - anything the user types next will fail identically.
      // Password-strength complaints are the one 400 that is worth retrying.
      const isTokenProblem =
        status === 400 && !/at least \d+ characters/i.test(message);

      if (isTokenProblem) {
        setLinkDead(true);
      } else {
        setError(message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showDeadEnd = !token || linkDead;

  return (
    <div className="min-h-screen w-full bg-card flex flex-col">
      {/* Top bar */}
      <div className="flex justify-between items-center px-6 sm:px-10 pt-6 pb-2">
        <Link to="/" className="flex items-center text-[2rem] font-bold font-roboto">
          <img src="/favicon.svg" alt="RepCheck logo" width="32" height="32" className="h-8 w-8 mr-2" />
          RepCheck
        </Link>
        <span className="text-lg font-semibold font-roboto">
          <Link to="/login" className="text-brand-600 hover:text-brand-700">Sign in</Link>
        </span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="bg-card border border-border rounded-2xl shadow-card-hover px-6 sm:px-10 py-10 flex flex-col items-center max-w-md w-full">
          {showDeadEnd ? (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-500/10">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h1 className="mt-4 text-2xl font-semibold text-foreground text-center">
                This link is no longer valid
              </h1>
              <p className="mt-2 text-base text-muted-foreground text-center">
                {!token
                  ? "This link is missing its token, so we can't tell which account it belongs to."
                  : "Password links can only be used once, and they expire. This one has already been used, replaced by a newer link, or run out of time."}
              </p>
              <p className="mt-3 text-sm text-muted-foreground text-center">
                Request a fresh one below - it works whether you're setting your password
                for the first time or resetting an existing one.
              </p>
              <Link
                to="/forgot-password"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-brand-700"
              >
                <LinkIcon className="h-4 w-4" /> Send me a new link
              </Link>
              <Link to="/login" className="mt-4 text-sm text-muted-foreground hover:text-foreground">
                Back to sign in
              </Link>
            </>
          ) : done ? (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/10">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="mt-4 text-2xl font-semibold text-foreground text-center">
                You're all set
              </h1>
              <p className="mt-2 text-base text-muted-foreground text-center">
                {typeof done === "string" ? done : "Your password has been changed."} Taking
                you to sign in…
              </p>
              <Link
                to="/login"
                className="mt-6 font-medium text-brand-600 hover:text-brand-700"
              >
                Sign in now
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-semibold text-foreground text-center mb-1">
                Choose your password
              </h1>
              <p className="text-base text-muted-foreground text-center mb-6">
                Pick something strong that you don't use anywhere else.
              </p>
              {error && (
                <div className="mb-4 w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200" role="alert">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="w-full space-y-4">
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min 8 characters)"
                  className="w-full rounded-lg border border-input px-4 py-3 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full rounded-lg border border-input px-4 py-3 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full rounded-lg py-3 font-semibold text-white transition-colors ${
                    submitting ? "cursor-not-allowed bg-neutral-400" : "bg-brand-600 hover:bg-brand-700"
                  }`}
                >
                  {submitting ? "Saving…" : "Set password"}
                </button>
              </form>
              <div className="mt-6 text-sm">
                <Link to="/login" className="text-muted-foreground hover:text-foreground">
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
